import { CREATE_DIALOG_ACTION_CLASS } from 'components/buttons/CancelButton';
import { DELETION_NOTICE_DAYS } from 'components/drafts/DraftChip';
import { DraftSavedAt } from 'components/drafts/DraftRecordActions';
import { TrashIcon, XIcon } from 'components/icons';
import { FormattedDate } from 'components/misc/FormattedDate';
import { Button } from 'components/ui/button';
import { Dialog, DialogClose, DialogContent, DialogTitle } from 'components/ui/dialog';
import { differenceInCalendarDays, parseISO } from 'date-fns';
import useAuth from 'hooks/useAuth';
import upperFirst from 'lodash/upperFirst';
import { isModifiedClick } from 'pages/ai-chat/components/message/isModifiedClick';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import { useDeleteDraftMutation, useGetDraftsQuery } from 'store/api/drafts';
import { DraftDetail, DraftType } from 'types/drafts';
import { successAlert } from 'utils/alert';
import { cn } from 'utils/cn';
import { getIntl, t } from 'utils/getTranslations';
import { displayName, useLastNameFirst } from 'utils/nameOrder';
import { createRecord, draftPageOf } from './recordTypes';

/** A draft's record type as Saved for later names it: "Case", "BD effort". */
export const draftTypeName = (type: DraftType): string => upperFirst(t(`create-dialog.record.${type}`));

/** Whether the draft is in its last week, of which its people have been told: as `DraftDeletionChip` counts it. */
export const deletesSoon = ({ deletesOn }: DraftDetail): boolean =>
    differenceInCalendarDays(parseISO(deletesOn), new Date()) <= DELETION_NOTICE_DAYS;

/**
 * How many days the tenant keeps a draft nobody saves, read off the drafts: the server dates a deletion its last
 * save's UTC day plus those days. The tenant configuration holding them is for back-office settings readers only.
 */
export const retentionDaysOf = ([draft]: DraftDetail[]): number | undefined =>
    draft &&
    differenceInCalendarDays(
        parseISO(draft.deletesOn),
        parseISO(new Date(draft.lastSavedAt).toISOString().slice(0, 10))
    );

/** Who shared the draft with the viewer: its creator, unless that is the viewer. `null` on one's own draft. */
export const sharerOf = ({ owner }: DraftDetail, userId: number | undefined, lastNameFirst: boolean): string | null =>
    owner && owner.id !== userId ? displayName(owner, lastNameFirst) : null;

/** Everyone the draft is shared with, as a list in words: "Kadri Tamm and Litigation". */
export const shareNames = ({ shares }: DraftDetail, lastNameFirst: boolean): string =>
    getIntl().formatList(
        shares.map((share) => displayName(share, lastNameFirst)),
        { type: 'conjunction' }
    );

/** When it was last saved, and what is left to fill before it can be created. */
export function SavedLine({ entry }: { entry: DraftDetail }) {
    return (
        <>
            <DraftSavedAt at={entry.lastSavedAt} />
            {' · '}
            <MissingLine entry={entry} />
        </>
    );
}

/** "2 required fields to fill", or "ready to create". */
export function MissingLine({ entry }: { entry: DraftDetail }) {
    return entry.missingFields.length > 0 ? (
        <FormattedMessage id="create-dialog.saved.to-fill" values={{ count: entry.missingFields.length }} />
    ) : (
        <FormattedMessage id="create-dialog.saved.ready" />
    );
}

/** Whom it is shared with, when it is the viewer's own; who shared it and how far, when it is not. */
export function ShareLine({ entry, userId }: { entry: DraftDetail; userId: number | undefined }) {
    const lastNameFirst = useLastNameFirst();
    const sharer = sharerOf(entry, userId, lastNameFirst);
    if (sharer) {
        return t('create-dialog.saved.shared-by', { name: sharer, right: entry.right.toLowerCase() });
    }
    return entry.shares.length > 0
        ? t('create-dialog.saved.shared-with', { names: shareNames(entry, lastNameFirst) })
        : t('create-dialog.saved.not-shared');
}

/** "Deleted on 22.10.2026 unless someone saves it". */
export function DeletesOnLine({ entry }: { entry: DraftDetail }) {
    return (
        <FormattedMessage
            id="draft.expires-on"
            values={{ date: <FormattedDate key="date" value={entry.deletesOn} /> }}
        />
    );
}

type RowProps = { entry: DraftDetail; userId: number | undefined; onOpen: () => void; onDelete: () => void };

/** A draft's row. Open is a link to the draft's page, which closes the list as it is followed. */
function Row({ entry, userId, onOpen, onDelete }: RowProps) {
    const { icon: Icon, tileClass } = createRecord(entry.type);
    const typeName = draftTypeName(entry.type);

    return (
        <li className="border-neutral-tint flex flex-wrap items-center gap-x-3 gap-y-2 border-b py-3 ps-6 pe-4">
            <span
                aria-hidden
                className={cn('flex size-7 shrink-0 items-center justify-center rounded-[7px]', tileClass)}
            >
                <Icon className="size-4" />
            </span>
            <span className="flex min-w-0 flex-[1_1_16rem] flex-col gap-0.5">
                <span className="truncate">
                    <span className="font-semibold">{typeName}</span>
                    {entry.name ? <span className="text-foreground-secondary"> · {entry.name}</span> : null}
                </span>
                <span className="text-meta text-muted-foreground">
                    <SavedLine entry={entry} />
                </span>
                <span className="text-meta text-muted-foreground">
                    <ShareLine entry={entry} userId={userId} />
                </span>
                {deletesSoon(entry) ? (
                    <span className="text-meta text-warning-ink">
                        <DeletesOnLine entry={entry} />
                    </span>
                ) : null}
            </span>
            <span className="flex items-center gap-1">
                <Button
                    variant="outline"
                    nativeButton={false}
                    role="link"
                    render={<Link to={draftPageOf(entry.type, entry.id)} />}
                    aria-label={`${t('create-dialog.saved.open')} ${entry.name ?? typeName}`}
                    className={cn(CREATE_DIALOG_ACTION_CLASS, 'h-8 px-3.5')}
                    // Drawn as a button, it opens on Space as on Enter: through the anchor's own click, which the Link
                    // follows. Base UI's Space would call onClick alone, and close the list without opening the draft.
                    onKeyDown={(event) => {
                        if (event.key === ' ') {
                            event.preventDefault();
                        }
                    }}
                    onKeyUp={(event) => {
                        if (event.key === ' ') {
                            event.preventBaseUIHandler();
                            event.currentTarget.click();
                        }
                    }}
                    // A modified click opens the draft elsewhere, and the list stays.
                    onClick={(event) => {
                        if (!isModifiedClick(event)) {
                            onOpen();
                        }
                    }}
                >
                    <FormattedMessage id="create-dialog.saved.open" />
                </Button>
                {entry.right === 'OWNER' ? (
                    <Button
                        variant="ghost"
                        size="icon-sm"
                        className="text-danger-ink"
                        aria-label={`${t('delete-draft')} ${entry.name ?? typeName}`}
                        onClick={onDelete}
                    >
                        <TrashIcon />
                    </Button>
                ) : (
                    <span aria-hidden className="size-8" />
                )}
            </span>
        </li>
    );
}

/** Deletes a draft for its owner and says so; `onLast` runs when it was the last one listed. */
export function useDeleteSavedDraft(count: number, onLast: () => void) {
    const [deleteDraft] = useDeleteDraftMutation();
    return (entry: DraftDetail) => {
        void deleteDraft({ type: entry.type, id: entry.id })
            .unwrap()
            .then(() => {
                successAlert(t('create-dialog.saved.deleted'));
                if (count === 1) {
                    onLast();
                }
            })
            .catch(() => undefined);
    };
}

/**
 * Saved for later: every draft the user sees, theirs and those shared with them, latest save first. Open goes to
 * the draft's page; its owner deletes it here. The list closes with its last row.
 */
export function SavedForLaterList({ onClose }: { onClose: () => void }) {
    const { user } = useAuth();
    const { data: saved = [] } = useGetDraftsQuery();
    const remove = useDeleteSavedDraft(saved.length, onClose);
    const retentionDays = retentionDaysOf(saved);

    return (
        <Dialog
            open
            onOpenChange={(isOpen) => {
                if (!isOpen) {
                    onClose();
                }
            }}
        >
            {/* Pinned where the Inbox beside it starts: a gap under the top bar. */}
            <DialogContent
                showCloseButton={false}
                className="top-(--panel-top) flex max-h-[calc(100dvh-var(--panel-top)-var(--panel-gap))] translate-y-0 flex-col gap-0 overflow-hidden p-0 sm:max-w-[600px]"
            >
                <div className="border-neutral-tint flex h-[60px] shrink-0 items-center gap-2.5 border-b ps-6 pe-3">
                    <DialogTitle className="text-dialog-title font-semibold">
                        <FormattedMessage id="create-dialog.saved.title" />
                    </DialogTitle>
                    <span className="text-meta text-muted-foreground flex-1 tabular-nums">
                        <FormattedMessage id="create-dialog.saved.count" values={{ count: saved.length }} />
                    </span>
                    <DialogClose render={<Button variant="ghost" size="icon" className="text-foreground-secondary" />}>
                        <XIcon className="size-[18px]" />
                        <span className="sr-only">
                            <FormattedMessage id="close" />
                        </span>
                    </DialogClose>
                </div>
                <ul className="min-h-0 flex-1 overflow-y-auto">
                    {saved.map((entry) => (
                        <Row
                            key={`${entry.type}/${entry.id}`}
                            entry={entry}
                            userId={user?.id}
                            onOpen={onClose}
                            onDelete={() => remove(entry)}
                        />
                    ))}
                </ul>
                {retentionDays === undefined ? null : (
                    <p className="bg-surface-tile text-muted-foreground text-caption px-6 py-2.5">
                        <FormattedMessage id="create-dialog.saved.note" values={{ days: retentionDays }} />
                    </p>
                )}
            </DialogContent>
        </Dialog>
    );
}
